import type { Date as PDate } from "../../generated/core/v1/date";
import type { Family } from "../../generated/core/v1/family";
import { EntityKind } from "../../generated/core/v1/financial_plan";
import { compareDates, monthsBetween } from "../lib/dates";
import { keptOver, type PaycheckSplit, paycheckSplit, spendingByCategory } from "./cashflow";
import { type Plan, endDate, netWorth, oweTotal, startDate, stateAt, firstState } from "./core";
import { debtFreeDate } from "./debt";
import { impactShares, priorityOutcomes } from "./priorities";
import { householdRetirement } from "./retirement";

/**
 * The impact screen and Home's headline numbers: the two plans side by side at the same date.
 */
export type Impact = {
  /** The date both plans are compared at (the household's retirement, else the plan end). */
  at?: PDate;
  atIsRetirement: boolean;
  netWorthToday: number;
  abiNetWorth: number;
  naiveNetWorth: number;
  /** abi − naive at `at`. */
  netWorthGain: number;
  /** Months sooner Abi clears the non-mortgage debt (negative = later), when both clear it. */
  expensiveDebtMonthsSooner?: number;
  expensiveDebtAbiMonths?: number;
  expensiveDebtNaiveMonths?: number;
  abiSplit: PaycheckSplit;
  naiveSplit: PaycheckSplit;
};

export const EXPENSIVE_DEBT_KINDS = new Set([EntityKind.CREDIT_CARD, EntityKind.LOAN]);

export function impact(family: Family | undefined | null, abi: Plan, naive: Plan): Impact {
  const ret = householdRetirement(abi, family);
  const at = ret?.date ?? endDate(abi);
  const a = netWorth(stateAt(abi, at));
  const n = naive ? netWorth(stateAt(naive, at)) : a;
  const from = startDate(abi);
  const months = (p: Plan) => {
    const d = debtFreeDate(p, EXPENSIVE_DEBT_KINDS);
    return d && from ? Math.max(0, monthsBetween(from, d)) : undefined;
  };
  const am = months(abi);
  const nm = naive ? months(naive) : undefined;
  return {
    at,
    atIsRetirement: !!ret,
    netWorthToday: netWorth(firstState(abi)),
    abiNetWorth: a,
    naiveNetWorth: n,
    netWorthGain: a - n,
    expensiveDebtAbiMonths: am,
    expensiveDebtNaiveMonths: nm,
    expensiveDebtMonthsSooner: am !== undefined && nm !== undefined ? nm - am : undefined,
    abiSplit: paycheckSplit(spendingByCategory(abi)),
    naiveSplit: paycheckSplit(spendingByCategory(naive)),
  };
}

// ------------------------------------------------------------------ Abi's impact (v44–v46)

/** Interest paid on every debt up to `until` (the latest interest-to-date each debt reports). */
function interestUntil(plan: Plan, until: PDate | undefined): number {
  const most = new Map<string, number>();
  plan?.monthlyStates.forEach((s) => {
    if (until && compareDates(s.date, until) > 0) return;
    s.debts.forEach((d) => {
      const v = d.interestPaidToDate ?? 0;
      if (v > (most.get(d.entityUuid) ?? 0)) most.set(d.entityUuid, v);
    });
  });
  let total = 0;
  most.forEach((v) => (total += v));
  return total;
}

/**
 * One before-and-after card on "Abi's impact": the current trajectory (`naive`, absent without that
 * run) against Abi's plan. Amounts in dollars; a priority's funding as completion 0..1.
 */
export type ImpactCard =
  | { kind: "retirement"; naive?: number; abi: number; at?: PDate; atIsRetirement: boolean }
  | { kind: "monthly"; naive?: number; abi: number }
  | { kind: "interest"; naive?: number; abi: number }
  | { kind: "debtFree"; naive?: PDate; abi?: PDate; end?: PDate }
  | { kind: "priority"; key: string; name: string; naive: number; abi: number };

/**
 * The cards "Abi's impact" shows (v46): net worth at retirement and what you keep each month on every
 * household's; interest and the debt-free date when there is debt; then the priorities that moved most
 * (Abi's own weighting, 2^importance × Δcompletion, `impactShares`), retirement aside, up to six cards.
 * Interest is compared up to the earlier end of the two runs (a run that stops at a shortfall pays no
 * interest after it).
 */
export function impactCards(
  family: Family | undefined | null,
  abi: Plan,
  naive: Plan,
): ImpactCard[] {
  const im = impact(family, abi, naive);
  // What you keep each month: money in less money out over the next 12 months, the same figure as Cash
  // Flow's headline (v45: the reveal and the dashboard quote one number). Counting only Abi's saving
  // and priority actions made the current path, whose money just piles up in chequing, keep nothing.
  const kept = (p: Plan) => keptOver(p).monthly;
  const cards: ImpactCard[] = [
    {
      kind: "retirement",
      naive: naive ? im.naiveNetWorth : undefined,
      abi: im.abiNetWorth,
      at: im.at,
      atIsRetirement: im.atIsRetirement,
    },
    { kind: "monthly", naive: naive ? kept(naive) : undefined, abi: kept(abi) },
  ];
  if (oweTotal(firstState(abi)) > 0.5) {
    const ends = [endDate(abi), naive ? endDate(naive) : undefined].filter((d): d is PDate => !!d);
    const until = ends.sort(compareDates)[0];
    cards.push(
      {
        kind: "interest",
        naive: naive ? interestUntil(naive, until) : undefined,
        abi: interestUntil(abi, until),
      },
      {
        kind: "debtFree",
        naive: naive ? debtFreeDate(naive) : undefined,
        abi: debtFreeDate(abi),
        end: endDate(abi),
      },
    );
  }
  if (naive) {
    impactShares(priorityOutcomes(family, abi, naive))
      .filter((g) => !g.outcome.isRetirement)
      .forEach((g) => {
        if (cards.length >= 6) return;
        cards.push({
          kind: "priority",
          key: g.outcome.key,
          name: g.outcome.name,
          naive: Math.min(1, g.outcome.naive?.completion ?? 0),
          abi: Math.min(1, g.outcome.abi?.completion ?? 0),
        });
      });
  }
  return cards.slice(0, 6);
}

/** How far Abi simulated: the plan's last year, and the days from its start to its end. */
export function simulatedSpan(plan: Plan): { endYear?: number; days?: number } {
  const from = startDate(plan);
  const to = endDate(plan);
  if (!from || !to) return {};
  const utc = (d: PDate) => Date.UTC(d.year, Math.max(0, d.month - 1), Math.max(1, d.day));
  return { endYear: to.year, days: Math.round((utc(to) - utc(from)) / 86_400_000) };
}
